'use client';

import { useEffect, useMemo, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useSiteTheme } from '@/components/SiteThemeProvider';
import { useVfx } from '@/components/VfxProvider';
import { useSiteSettings } from '@/lib/swr/hooks/useSiteSettings';
import { getSiteBackgroundMode, mountSiteBackground, type SiteBackgroundMode } from '@/lib/siteBackground';
import { normalizeBackgroundSettings, type BackgroundConfig } from '@/lib/siteBackgroundSettings';
import { normalizeSiteThemes, resolveSiteTheme, type SiteThemeId } from '@/lib/siteTheme';
import styles from './SiteBackground.module.css';

export function SiteBackgroundSurface({ mode, preview = false, playing = false, config: draft, theme: previewTheme }: {
  mode: Exclude<SiteBackgroundMode, 'off'>; preview?: boolean; playing?: boolean; config?: BackgroundConfig; theme?: SiteThemeId;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const current = useSiteTheme();
  const { tier } = useVfx();
  const { settings } = useSiteSettings();
  const config = useMemo(() => draft ?? normalizeBackgroundSettings(settings?.site_background).config, [draft, settings?.site_background]);
  const theme = previewTheme ?? current.theme;
  const tokens = previewTheme ? resolveSiteTheme(theme, normalizeSiteThemes(settings?.site_themes).themes[theme].overrides) : current.tokens;

  useEffect(() => {
    if (mode !== 'featured' || !canvasRef.current) return;
    return mountSiteBackground(canvasRef.current, { config, theme, background: tokens.bg, ink: tokens.text,
      tier, preview, motion: preview ? playing : true });
  }, [mode, preview, playing, tier, config, theme, tokens.bg, tokens.text]);

  return <div className={`${styles.background} ${preview ? styles.preview : ''} ${preview && playing ? styles.interactive : ''}`}
    data-site-background={mode} aria-hidden="true">
    {/* Один canvas и одинаковая SSR-разметка даже при автоматическом VFX off. */}
    {mode === 'featured' ? <canvas ref={canvasRef} data-site-background-canvas="true" /> :
      <svg viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice" className={styles.quiet}>
        <path d="M-40,610 C70,580 55,730 135,750 S170,810 240,830 M70,668 C30,715 40,755 -20,770 M1030,50 C930,70 995,180 925,200 S955,320 1020,350 M952,155 C890,135 910,90 850,70" />
      </svg>}
  </div>;
}

export default function SiteBackground() {
  const pathname = usePathname();
  const mode = getSiteBackgroundMode(pathname || '');
  return mode === 'off' ? null : <SiteBackgroundSurface key={pathname} mode={mode} />;
}
